import { z } from 'zod'

// 목록 API 응답 - listType=ALL
export const ticketSubscribeListAllSchema = z.object({
  subscribe_id: z.string(),
  subscribe_mapp_id: z.string(),
  ticket_ids: z.string(),
  name: z.string(),
  subscribe_status: z.string(),
  subscribe_type: z.string(),
  cur_payment_count: z.number(),
  total_payment_count: z.number(),
  start_date: z.string(),
  end_date: z.string(),
  next_payment_date: z.string().nullable(),
  next_payment_amount: z.number().nullable(),
  resubscribe_yn: z.string(),
  lesson_count_per_month: z.number(),
})

export type TicketSubscribeListAll = z.infer<typeof ticketSubscribeListAllSchema>

// 플랜 상태 그룹
export type PlanGroup = {
  using: TicketSubscribeListAll[]
  bonus: TicketSubscribeListAll[]
  finish: TicketSubscribeListAll[]
}

// 상세 API 응답
export const ticketSubscribeDetailSchema = z.object({
  name: z.string(),
  subscribe_type: z.string(),
  subscribe_id: z.string(),
  product_id: z.string().nullable().optional(),
  subscribe_mapp_id: z.string(),
  cur_payment_count: z.number(),
  total_payment_count: z.number(),
  start_date: z.string(),
  end_date: z.string(),
  next_payment_date: z.string().nullable(),
  lang_type: z.string(),
  lesson_time: z.number(),
  lesson_count_per_month: z.number(),
  finished_class_count: z.number(),
  holding_start_date: z.number().nullable(),
  holding_end_date: z.number().nullable(),
  payment_method: z.string().nullable(),
  payment_at: z.number().nullable(),
  subscribe_price: z.number(),
  cancel_yn: z.string(),
  resubscribe_yn: z.string(),
  curriculum_type: z.string(),
  certified: z.boolean(),
  completed_chat: z.number().optional(),
  total_chat_count: z.number().optional(),
  subscribe_status: z.string().optional(),
  // 홀딩 잔여/한도 (백엔드 holdingBalance). 구버전 응답/체험권 등에서는 없을 수 있어 optional·nullable
  holding_balance: z
    .object({
      base_days: z.number(),
      bonus_days: z.number(),
      total_days: z.number(),
      used_days: z.number(),
      remaining_days: z.number(),
      base_count: z.number(),
      bonus_count: z.number(),
      max_count: z.number(),
      used_count: z.number(),
      remaining_count: z.number(),
    })
    .nullable()
    .optional(),
})

export type TicketSubscribeDetail = z.infer<typeof ticketSubscribeDetailSchema>

// subscribe-mapp API 응답 타입
export const ticketDtoSchema = z.object({
  id: z.string(),
  reason: z.string().nullable(),
  originCount: z.number(),
  purchasedCount: z.number(),
  usedCount: z.number(),
  destroyCount: z.number(),
  refundCount: z.number(),
  remainCountNum: z.number(),
  remainCount: z.string(),
  ticketStartDate: z.string(),
  ticketExpireDate: z.string(),
  ticketStartDatetime: z.string().nullish(),
  ticketExpireDatetime: z.string().nullish(),
  eventType: z.string().nullable(),
  paymentLangType: z.string().nullable(),
  curriculumType: z.string().nullable(),
  langType: z.string().nullable(),
  langTypeName: z.string().nullable(),
  subscribeMappId: z.string().nullable(),
  goodsName: z.string().nullable(),
  paymentId: z.number().nullable(),
  lessonTime: z.number().nullable(),
  isCertified: z.boolean().optional().default(false),
})

export type TicketDto = z.infer<typeof ticketDtoSchema>

export const subscribeMappListDtoSchema = z.object({
  subscribeMappId: z.string(),
  subscribeId: z.string(),
  productKind: z.string().nullish(),
  validityMonths: z.number().int().min(1).max(12).nullish(),
  productId: z.string().nullable().optional(),
  bundle: z.boolean().optional(),
  name: z.string(),
  subscribeType: z.string(),
  subscribeStatus: z.string(),
  status: z.string(),
  langType: z.string().nullable(),
  curriculumType: z.string().nullable(),
  lessonTime: z.number(),
  lessonCountPerMonth: z.number(),
  curPaymentCount: z.number(),
  totalPaymentCount: z.number(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  nextPaymentDate: z.string().nullable(),
  nextPaymentAmount: z.number().nullable(),
  createdDate: z.string().nullable(),
  activeTicket: ticketDtoSchema.nullable(),
  purchaseDate: z.string().nullable(),
  totalLessonCount: z.number().nullable(),
  applyType: z.string().nullable().optional(),
})

export type SubscribeMappListDto = z.infer<typeof subscribeMappListDtoSchema>

// subscribe-mapp/{id} 상세 API 응답 타입
export const subscribeMappDetailDtoSchema = z.object({
  subscribeMappId: z.string(),
  subscribeId: z.string(),
  productKind: z.string().nullish(),
  validityMonths: z.number().int().min(1).max(12).nullish(),
  totalLessonCount: z.number().int().nonnegative().nullish(),
  productId: z.string().nullable().optional(),
  bundle: z.boolean().optional(),
  name: z.string(),
  subscribeType: z.string(),
  subscribeStatus: z.string(),
  status: z.string(),
  langType: z.string(),
  curriculumType: z.string(),
  lessonTime: z.number().nullable(),
  lessonCountPerMonth: z.number().nullable(),
  curPaymentCount: z.number().nullable(),
  totalPaymentCount: z.number().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  nextPaymentDate: z.string().nullable(),
  nextPaymentAmount: z.number().nullable(),
  tickets: z.array(ticketDtoSchema),
  paymentMethod: z.string().nullable(),
  paymentAt: z.number().nullable(),
  paymentAmount: z.number().nullable(),
  // 홀딩 잔여/한도 (백엔드 holdingBalance, toCamelCase 변환됨). 구버전/체험권 등에서는 없을 수 있어 optional·nullable
  holdingBalance: z
    .object({
      baseDays: z.number(),
      bonusDays: z.number(),
      totalDays: z.number(),
      usedDays: z.number(),
      remainingDays: z.number(),
      baseCount: z.number(),
      bonusCount: z.number(),
      maxCount: z.number(),
      usedCount: z.number(),
      remainingCount: z.number(),
    })
    .nullable()
    .optional(),
})

export type SubscribeMappDetailDto = z.infer<typeof subscribeMappDetailDtoSchema>

export const selfHoldingStatusSchema = z.enum(['CONFIRMED', 'UPCOMING', 'ACTIVE', 'COMPLETED', 'CANCELLED'])
export type SelfHoldingStatus = z.infer<typeof selfHoldingStatusSchema>

export const selfHoldingBalanceSchema = z.object({
  baseDays: z.number(),
  bonusDays: z.number(),
  totalDays: z.number(),
  usedDays: z.number(),
  remainingDays: z.number(),
  baseCount: z.number(),
  bonusCount: z.number(),
  maxCount: z.number(),
  usedCount: z.number(),
  remainingCount: z.number(),
})
export type SelfHoldingBalance = z.infer<typeof selfHoldingBalanceSchema>

export const selfHoldingItemSchema = z.object({
  holdingId: z.number(),
  status: selfHoldingStatusSchema,
  startDate: z.string(),
  endDate: z.string(),
  days: z.number(),
  cancellable: z.boolean(),
})
export type SelfHoldingItem = z.infer<typeof selfHoldingItemSchema>

export const selfHoldingsDtoSchema = z.object({
  holdingBalance: selfHoldingBalanceSchema,
  holdings: z.array(selfHoldingItemSchema),
})
export type SelfHoldingsDto = z.infer<typeof selfHoldingsDtoSchema>

export const selfHoldingConflictItemSchema = z.object({
  lectureId: z.number(),
  lessonDate: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  tutorName: z.string().nullable(),
})
export type SelfHoldingConflictItem = z.infer<typeof selfHoldingConflictItemSchema>

export const selfHoldingMutationResultSchema = z.object({
  holdingId: z.number(),
  status: selfHoldingStatusSchema,
  startDate: z.string(),
  endDate: z.string(),
  days: z.number(),
  holdingBalance: selfHoldingBalanceSchema,
})
export type SelfHoldingMutationResult = z.infer<typeof selfHoldingMutationResultSchema>
